import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { userSelector } from "../../store/selectors";
import "./NotificationsScreen.css";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import uncheckedIcon from "../../assets/unchecked.svg";
import checkedIcon from "../../assets/checked.svg";
import AppLogo from "../../assets/AppLogoBlue.svg";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import {
  fetchNotificationsSettings,
  updateNotificationSetting,
} from "../../store/appSlice";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import useIsMobile from "../../hooks/useIsMobile";

const notificationTypeNames = {
  ACCESS_GRANTED: "Получение доступа к проекту или видео",
  VERSION_READY: "Загрузка нового видео",
  ROOT_COMMENT_CREATED: "Комментарии к видео",
  COMMENT_REPLIED: "Ответы на комментарии",
};

const notificationSettingsNames = {
  EMAIL: "На электронную почту",
  PUSH: "В колокольчик",
};

const NotificationsScreen = () => {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [notificationsSettings, setNotificationsSettings] = useState({});
  const [loadingNotificationsIds, setLoadingNotificationsIds] = useState([]);
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "7cut • Уведомления";
  }, []);

  useEffect(() => {
    updateSettings();
  }, []);

  const updateSettings = () => {
    dispatch(fetchNotificationsSettings()).then((res) => {
      if (!res.error) {
        const permissionsArray = res.payload;
        const accessGrantedNotifications = permissionsArray.filter(
          (permission) => permission.eventType === "ACCESS_GRANTED"
        );
        const versionReadyNotifications = permissionsArray.filter(
          (permission) => permission.eventType === "VERSION_READY"
        );
        const rootCommentNotifications = permissionsArray.filter(
          (permission) => permission.eventType === "ROOT_COMMENT_CREATED"
        );
        const commentRepliedNotifications = permissionsArray.filter(
          (permission) => permission.eventType === "COMMENT_REPLIED"
        );
        const resultObject = {
          ACCESS_GRANTED: accessGrantedNotifications,
          VERSION_READY: versionReadyNotifications,
          ROOT_COMMENT_CREATED: rootCommentNotifications,
          COMMENT_REPLIED: commentRepliedNotifications,
        };
        setNotificationsSettings(resultObject);
      }
    });
  };

  if (!user) return null;

  const onClickSwitchSetting = (setting) => {
    if (
      loadingNotificationsIds.find(
        (loadingId) => loadingId === `${setting.eventTypeId}_${setting.channel}`
      )
    ) {
      return;
    }
    const { eventTypeId, channel } = setting;
    loadingNotificationsIds.push(`${setting.eventTypeId}_${setting.channel}`);
    dispatch(
      updateNotificationSetting({
        eventTypeId,
        channel,
        enabled: !setting.enabled,
      })
    ).then((res) => {
      if (!res.error) {
        const loadingIndex = loadingNotificationsIds.findIndex(
          (loadingId) =>
            loadingId === `${setting.eventTypeId}_${setting.channel}`
        );
        if (loadingIndex > -1) {
          const newLoadingNotificationsIds = [...loadingNotificationsIds];
          newLoadingNotificationsIds.splice(loadingIndex, 1);
          setLoadingNotificationsIds(newLoadingNotificationsIds);
        }
        updateSettings();
      }
    });
  };

  const renderSettingsBlock = (notificationType, settingsArray) => {
    return (
      <div className="notifications-settings-block">
        <span className="notifications-settings-subtitle">
          {notificationTypeNames[notificationType]}
        </span>
        {settingsArray.map((setting) => (
          <div
            className="setting-row"
            onClick={() => onClickSwitchSetting(setting)}
          >
            <img
              src={setting.enabled ? checkedIcon : uncheckedIcon}
              alt="options"
              style={{
                width: 24,
                height: 24,
              }}
            />
            <span className="setting-title">
              {notificationSettingsNames[setting.channel]}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={isMobile ? "App-mobile" : "App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "mainScreenHeader"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      {/* LAYOUT: sidebar + main content */}
      <div
        className="notifications-container"
        style={{ alignItems: "flex-start" }}
      >
        {/* SIDEBAR */}
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="notifications" />
        )) ||
          null}
        {/* MAIN CONTENT */}
        <main
          className={
            isMobile ? "notifications-main-mobile" : "notifications-main"
          }
        >
          <h1 className="notifications-title">Уведомления</h1>
          {Object.keys(notificationsSettings).map((notificationType) =>
            renderSettingsBlock(
              notificationType,
              notificationsSettings[notificationType]
            )
          )}
        </main>
      </div>
    </div>
  );
};

export default NotificationsScreen;
